import * as React from "react";
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControlLabel from '@mui/material/FormControlLabel';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { Link } from "react-router-dom";
import moment from 'moment';
import Grid from '@mui/material/Grid';
import CustomSelect from "../CustomComponents/CustomSelect";

export default function AppRoleDialog({ handleDialogCallback }) {
    const [refresh, setRefresh] = React.useState(true);
    const [open, setOpen] = React.useState(false);

    const initialValues = {
        Name: '',
        IsActive: false,
        ViewFinancials: false, 
        GlobalAccess: false, 
        CanBeAssignedTo: false, 
        CreateEvent: false,
        ViewAppRoles: false,
        CreateAppRoles: false,
        ViewUser: false, 
        CreateUser: false,
        ViewUserRoles: false,
        EditUserRoles: false,
        CreateUserRoles: false,
        ViewUserProperties: false,
        CreateUserProperties: false,
        ViewEntry: false, 
        CreateEntry: false, 
        ViewSOW: false,
        CreateSOW: false,
        ViewCostCenter: false, 
        CreateCostCenter: false,
        ViewPo: false,
        CreatePo: false,
        ViewLab: false,
        CreateLab: false,
        ViewProjects: false,
        CreateProjects: false,
        ViewTasks: false,
        CreateTasks: false,
        ViewTaskGroup: false,
        CreateTaskGroup: false,
        ViewTaskVariables: false,
        CreateTaskVariables: false,
        CreateInvoice: false,
        CreateExpense: false,
        ViewPosition: false,
        CreatePosition: false,
        CreateRate: false,
        CreateClientSite: false
    };

    const validFormSchema = Yup.object({
        Name: Yup
            .string('Enter a name for the position')
            .required('Position name is required'),
        IsActive: Yup
            .boolean(),
        ViewFinancials: Yup
            .boolean(),
        GlobalAccess: Yup
            .boolean(),
        CanBeAssignedTo: Yup
            .boolean(),
        CreateEvent: Yup
            .boolean(),
        ViewAppRoles: Yup
            .boolean(),
        CreateAppRoles: Yup
            .boolean(),
        ViewUser: Yup
            .boolean(),
        CreateUser: Yup
            .boolean(),
        ViewUserRoles: Yup
            .boolean(),
        EditUserRoles: Yup
            .boolean(),
        CreateUserRoles: Yup
            .boolean(),
        ViewUserProperties: Yup
            .boolean(),
        CreateUserProperties: Yup
            .boolean(),
        ViewEntry: Yup
            .boolean(),
        CreateEntry: Yup
            .boolean(),
        ViewSOW: Yup
            .boolean(),
        CreateSOW: Yup
            .boolean(),
        ViewCostCenter: Yup
            .boolean(),
        CreateCostCenter: Yup
            .boolean(),
        ViewPo: Yup
            .boolean(),
        CreatePo: Yup
            .boolean(),
        ViewLab: Yup
            .boolean(),
        CreateLab: Yup
            .boolean(),
        ViewProjects: Yup
            .boolean(),
        CreateProjects: Yup
            .boolean(),
        ViewTasks: Yup
            .boolean(),
        CreateTasks: Yup
            .boolean(),
        ViewTaskGroup: Yup
            .boolean(),
        CreateTaskGroup: Yup
            .boolean(),
        ViewTaskVariables: Yup
            .boolean(),
        CreateTaskVariables: Yup
            .boolean(),
        CreateInvoice: Yup
            .boolean(),
        CreateExpense: Yup
            .boolean(),
        ViewPosition: Yup
            .boolean(),
        CreatePosition: Yup
            .boolean(),
        CreateRate: Yup
            .boolean(),
        CreateClientSite: Yup
        	.boolean()
    })

    const onTrigger = React.useCallback((values) => {
        const msg = {
            AppRoleName: values.Name,
            IsActive: values.IsActive,
            ViewFinancials: values.ViewFinancials,
            GlobalAccess: values.GlobalAccess,
            CanBeAssignedTo: values.CanBeAssignedTo,
            CreateEvent: values.CreateEvent,
            ViewAppRoles: values.ViewAppRoles,
            CreateAppRoles: values.CreateAppRoles,
            ViewUser: values.ViewUser,
            CreateUser: values.CreateUser,
            ViewUserRoles: values.ViewUserRoles,
            EditUserRoles: values.EditUserRoles,
            CreateUserRoles: values.CreateUserRoles,
            ViewUserProperties: values.ViewUserProperties,
            CreateUserProperties: values.CreateUserProperties,
            ViewEntry: values.ViewEntry,
            CreateEntry: values.CreateEntry,
            ViewSOW: values.ViewSOW,
            CreateSOW: values.CreateSOW,
            ViewCostCenter: values.ViewCostCenter,
            CreateCostCenter: values.CreateCostCenter,
            ViewPO: values.ViewPo,
            CreatePO: values.CreatePo,
            ViewLab: values.ViewLab,
            CreateLab: values.CreateLab,
            ViewProjects: values.ViewProjects,
            CreateProjects: values.CreateProjects,
            ViewTasks: values.ViewTasks,
            CreateTasks: values.CreateTasks,
            ViewTaskGroup: values.ViewTaskGroup,
            CreateTaskGroup: values.CreateTaskGroup,
            ViewTaskVariables: values.ViewTaskVariables,
            CreateTaskVariables: values.CreateTaskVariables,
            CreateInvoice: values.CreateInvoice,
            CreateExpense: values.CreateExpense,
            ViewPosition: values.ViewPosition,
            CreatePosition: values.CreatePosition,
            CreateRate: values.CreateRate,
            CreateClientSite: values.CreateClientSite
        };

        handleDialogCallback(msg);
        handleClose();
        setRefresh(false);
    })

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };


    return (
        <div>
            <Button variant="contained" onClick={handleClickOpen}>
                Add App Role
            </Button>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">                
                <DialogContent>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validFormSchema}
                        onSubmit={(values, { resetForm }) => {
                            onTrigger(values);
                            resetForm();
                        }}
                    >
                        {props => (
                            <Form>                                
                                <AppBar sx={{ position: 'relative' }}>
                                    <Toolbar>
                                        <IconButton
                                            edge="start"
                                            color="inherit"
                                            onClick={handleClose}
                                            aria-label="close"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                        <Typography sx={{ ml: 2, flex: 1, color:"white" }} variant="h6" component="div" >
                                            App Role
                                        </Typography>
                                       <Button autoFocus color="inherit" onClick={props.handleSubmit}>
                                            save
                                        </Button>
                                    </Toolbar>
                                </AppBar>
                                <DialogTitle>App Role Info:</DialogTitle>
                                <div>
                                    <Typography variant="h4" sx={{ m: 5 }} color="common.black">
                                        Role Name/Title:
                                    </Typography>
                                    <FormControl sx={{ m: 1, width: 220 }}>
                                        <TextField
                                            name="Name"
                                            id="outlined-name"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.Name}
                                            error={props.touched.Name && Boolean(props.errors.Name)}
                                            helperText={props.touched.Name && props.errors.Name}
                                            label="Name"
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="IsActive"
                                            label="Active"
                                            control={<Checkbox checked={props.values.IsActive} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                </div>
                                <div>

                                    <Typography variant="h4" sx={{ m: 5 }} color="common.black">
                                        Permissions:
                                    </Typography>
                                    <Grid container rowSpacing={1} columnSpacing={1}>
                                        <Grid item xs={2}> 
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewFinancials"
                                            label="ViewFinancials"
                                            control={<Checkbox checked={props.values.ViewFinancials} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="GlobalAccess"
                                            label="GlobalAccess"
                                            control={<Checkbox checked={props.values.GlobalAccess} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CanBeAssignedTo"
                                            label="CanBeAssignedTo"
                                            control={<Checkbox checked={props.values.CanBeAssignedTo} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateEvent"
                                            label="CreateEvent"
                                            control={<Checkbox checked={props.values.CreateEvent} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewAppRoles"
                                            label="ViewAppRoles"
                                            control={<Checkbox checked={props.values.ViewAppRoles} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateAppRoles"
                                            label="CreateAppRoles"
                                            control={<Checkbox checked={props.values.CreateAppRoles} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewUser"
                                            label="ViewUser"
                                            control={<Checkbox checked={props.values.ViewUser} onChange={props.handleChange} />}
                                        />
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={2}>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateUser"
                                            label="CreateUser"
                                            control={<Checkbox checked={props.values.CreateUser} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewUserRoles"
                                            label="ViewUserRoles"
                                            control={<Checkbox checked={props.values.ViewUserRoles} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="EditUserRoles"
                                            label="EditUserRoles"
                                            control={<Checkbox checked={props.values.EditUserRoles} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateUserRoles"
                                            label="CreateUserRoles"
                                            control={<Checkbox checked={props.values.CreateUserRoles} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewUserProperties"
                                            label="ViewUserProperties"
                                            control={<Checkbox checked={props.values.ViewUserProperties} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateUserProperties"
                                            label="CreateUserProperties"
                                            control={<Checkbox checked={props.values.CreateUserProperties} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewEntry"
                                            label="ViewEntry"
                                            control={<Checkbox checked={props.values.ViewEntry} onChange={props.handleChange} />}
                                                />                                     
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={2}> 
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateEntry"
                                            label="CreateEntry"
                                            control={<Checkbox checked={props.values.CreateEntry} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewSOW"
                                            label="ViewSOW"
                                            control={<Checkbox checked={props.values.ViewSOW} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateSOW"
                                            label="CreateSOW"
                                            control={<Checkbox checked={props.values.CreateSOW} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewCostCenter"
                                            label="ViewCostCenter"
                                            control={<Checkbox checked={props.values.ViewCostCenter} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateCostCenter"
                                            label="CreateCostCenter"
                                            control={<Checkbox checked={props.values.CreateCostCenter} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewPo"
                                            label="ViewPo"
                                            control={<Checkbox checked={props.values.ViewPo} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreatePo"
                                            label="CreatePo"
                                            control={<Checkbox checked={props.values.CreatePo} onChange={props.handleChange} />}
                                            />                                
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={2}> 
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewLab"
                                            label="ViewLab"
                                            control={<Checkbox checked={props.values.ViewLab} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateLab"
                                            label="CreateLab"
                                            control={<Checkbox checked={props.values.CreateLab} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewProjects"
                                            label="ViewProjects"
                                            control={<Checkbox checked={props.values.ViewProjects} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateProjects"
                                            label="CreateProjects"
                                            control={<Checkbox checked={props.values.CreateProjects} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewTasks"
                                            label="ViewTasks"
                                            control={<Checkbox checked={props.values.ViewTasks} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateTasks"
                                            label="CreateTasks"
                                            control={<Checkbox checked={props.values.CreateTasks} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewTaskGroup"
                                            label="ViewTaskGroup"
                                            control={<Checkbox checked={props.values.ViewTaskGroup} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2}> 
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateTaskGroup"
                                            label="CreateTaskGroup"
                                            control={<Checkbox checked={props.values.CreateTaskGroup} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewTaskVariables"
                                            label="ViewTaskVariables"
                                            control={<Checkbox checked={props.values.ViewTaskVariables} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateTaskVariables"
                                            label="CreateTaskVariables"
                                            control={<Checkbox checked={props.values.CreateTaskVariables} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateInvoice"
                                            label="CreateInvoice"
                                            control={<Checkbox checked={props.values.CreateInvoice} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateExpense"
                                            label="CreateExpense"
                                            control={<Checkbox checked={props.values.CreateExpense} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="ViewPosition"
                                            label="ViewPosition"
                                            control={<Checkbox checked={props.values.ViewPosition} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreatePosition"
                                            label="CreatePosition"
                                            control={<Checkbox checked={props.values.CreatePosition} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2}> 
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateRate"
                                            label="CreateRate"
                                            control={<Checkbox checked={props.values.CreateRate} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                    <FormControl sx={{ m: 1 }}>
                                        <FormControlLabel
                                            name="CreateClientSite"
                                            label="CreateClientSite"
                                            control={<Checkbox checked={props.values.CreateClientSite} onChange={props.handleChange} />}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </div>
    );
}